import { Modal, Button } from './ui';
import type { RazorpayError } from '../types';

interface PaymentErrorModalProps {
  error: RazorpayError | null;
  onDismiss: () => void;
  onRetry?: () => void;
}

export const PaymentErrorModal = ({ 
  error,
  onDismiss, 
  onRetry
}: PaymentErrorModalProps) => {
  if (!error) return null;

  // Determine if payment can be retried based on error type
  const canRetry = !error.error.code.includes('CANCELLED') && 
                   !error.error.code.includes('INITIATION_FAILED') &&
                   error.error.source !== 'backend';
  
  // Get user-friendly error message
  const getErrorMessage = () => {
    if (error.error.code === 'PAYMENT_CANCELLED') {
      return 'Payment was cancelled. You can try again when ready.';
    }
    if (error.error.code === 'INITIATION_FAILED') {
      return 'Payment could not be started. Please try again.';
    }
    if (error.error.code === 'CONFIRMATION_FAILED') {
      return 'Payment succeeded but confirmation failed. Please contact support.';
    }
    if (error.error.source === 'bank') {
      return 'Payment was declined by your bank. Try a different payment method.';
    }
    if (error.error.code === 'BAD_REQUEST_ERROR') {
      return 'Payment failed. Please try a different payment method.';
    }
    return error.error.description || 'Payment failed. Please try again.';
  };
  
  const errorMessage = getErrorMessage();

  const getErrorIcon = () => {
    if (error.error.code.includes('CANCELLED')) return '🚫';
    if (error.error.code.includes('NETWORK')) return '📶';
    if (error.error.code.includes('INSUFFICIENT')) return '💳';
    if (error.error.code.includes('CARD')) return '💳';
    return '❌';
  };

  const getErrorTitle = () => {
    if (error.error.code.includes('CANCELLED')) return 'Payment Cancelled';
    if (error.error.code.includes('NETWORK')) return 'Network Error';
    if (error.error.code.includes('INSUFFICIENT')) return 'Insufficient Balance';
    if (error.error.code.includes('CARD')) return 'Card Error';
    return 'Payment Failed';
  };

  return (
    <Modal
      isOpen={true}
      onClose={onDismiss}
      title="Payment Error"
      closeOnBackdrop={false}
    >
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">{getErrorIcon()}</span>
        </div>
        <h3 className="text-lg font-semibold text-red-600 mb-2">
          {getErrorTitle()}
        </h3>
        <p className="text-gray-600">
          {errorMessage}
        </p>
      </div>

      {/* Error Details */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-red-800 font-medium mb-1">Error Details:</p>
        <p className="text-sm text-red-700 mb-1">
          <strong>Code:</strong> {error.error.code}
        </p>
        <p className="text-sm text-red-700 mb-1">
          <strong>Step:</strong> {error.error.step}
        </p>
        {error.error.reason && (
          <p className="text-sm text-red-700">
            <strong>Reason:</strong> {error.error.reason}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex space-x-3">
        {canRetry && onRetry && (
          <Button
            onClick={() => {
              onDismiss();
              onRetry();
            }}
            variant="primary"
            className="flex-1"
          >
            Try Again
          </Button>
        )}
        
        <Button
          onClick={onDismiss}
          variant={canRetry ? "outline" : "primary"}
          className="flex-1"
        >
          {canRetry ? 'Cancel' : 'Close'}
        </Button>
      </div>

      {/* Help Text */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          <strong>Need help?</strong> If you continue to face issues, please contact support. 
          Your charges remain unchanged and you can try paying again.
        </p>
      </div>
    </Modal>
  );
};